import React from "react";
import { Link } from "react-router-dom";
import { createPageStyles } from "./MarketingPageStyles";
import { detectPageLocale } from "./pageLocale";

const styles = createPageStyles("dark", "luna");

type Locale = "ko" | "en" | "ja";

const LOCALE_STORAGE_KEY = "addup-terms-locale";
const locales: Locale[] = ["ko", "en", "ja"];
const localeLabels: Record<Locale, string> = { ko: "KO", en: "EN", ja: "JA" };

const content: Record<
  Locale,
  {
    crumb: string;
    title: string;
    accentTitle: string;
    subtitle: string;
    sections: Array<{ title: string; body?: string; list?: string[] }>;
  }
> = {
  ko: {
    crumb: "AddUp Terms",
    title: "AddUp,",
    accentTitle: "이용약관",
    subtitle:
      "최종 업데이트일: 2026년 6월 14일. 본 서비스의 운영 주체는 Daydream Factory입니다. AddUp 앱과 관련 웹페이지를 이용하는 경우 아래 약관이 적용됩니다.",
    sections: [
      {
        title: "1. 서비스 소개",
        body:
          "AddUp은 별도의 회원가입 없이 사용할 수 있는 할 일·일정 관리 앱입니다. 작성한 할 일은 기기 안에 저장되며, 사용자의 iCloud 계정을 통해 같은 사용자의 기기 간에 동기화됩니다.",
      },
      {
        title: "2. 구독 (AddUp Pro)",
        list: [
          "AddUp은 무료로 사용할 수 있으며, 반복 일정·미리 알림·상세 통계 등 일부 기능은 자동 갱신 구독(AddUp Pro)에서 제공됩니다.",
          "구독 가격과 기간(월간/연간), 무료 체험 기간은 결제 화면 및 App Store에 표시된 내용을 따릅니다.",
          "구독은 자동으로 갱신되며, 현재 기간이 끝나기 최소 24시간 전에 해지하지 않으면 동일 조건으로 갱신되고 요금이 청구됩니다.",
          "구독 관리·해지는 기기의 App Store 계정 설정에서 직접 하실 수 있습니다. 결제·환불은 Apple App Store 정책에 따릅니다.",
          "무료 체험 기간이 제공되는 경우, 체험 기간 종료 전에 해지하지 않으면 유료 구독으로 전환됩니다.",
        ],
      },
      {
        title: "3. 데이터와 사용자 책임",
        list: [
          "할 일 데이터는 기기 로컬과 사용자 본인의 iCloud에 저장되며, Daydream Factory는 해당 내용에 접근하지 않습니다.",
          "기기 교체·앱 삭제·iCloud 설정 변경 시 데이터가 사라질 수 있으므로, 중요한 내용은 사용자가 별도로 관리·백업할 책임이 있습니다.",
          "앱 내 '전체 초기화' 기능을 사용하면 기기 내 데이터가 삭제됩니다.",
        ],
      },
      {
        title: "4. 면책",
        list: [
          "AddUp은 '있는 그대로' 제공되며, 알림 전송 시점·동기화는 운영체제, 통신 환경, 외부 서비스 상태에 따라 지연되거나 제한될 수 있습니다.",
          "본 서비스 이용 또는 데이터 유실로 발생한 손해에 대해 관련 법령이 허용하는 범위 내에서 책임이 제한될 수 있습니다.",
        ],
      },
      {
        title: "5. 지적재산권",
        body:
          "AddUp 앱과 디자인, 로고, 콘텐츠에 대한 권리는 Daydream Factory에 있으며, 무단 복제·배포·역설계를 금지합니다.",
      },
      {
        title: "6. 약관 변경",
        body:
          "본 약관은 서비스 개선 또는 법령 변경에 따라 수정될 수 있으며, 중요한 변경은 앱 또는 본 페이지를 통해 안내합니다.",
      },
      {
        title: "7. 문의",
        body: "본 약관에 관한 문의: support@daydreamfactory.net",
      },
    ],
  },
  en: {
    crumb: "AddUp Terms",
    title: "AddUp,",
    accentTitle: "Terms of Service",
    subtitle:
      "Last updated: June 14, 2026. This service is operated by Daydream Factory. The following terms apply when you use the AddUp app and related web pages.",
    sections: [
      {
        title: "1. About the Service",
        body:
          "AddUp is a to-do and schedule app you can use without creating an account. Your tasks are stored on your device and synced across your own devices through your iCloud account.",
      },
      {
        title: "2. Subscription (AddUp Pro)",
        list: [
          "AddUp is free to use. Some features such as recurring schedules, reminders, and detailed statistics are provided through an auto-renewable subscription (AddUp Pro).",
          "Subscription price, period (monthly/yearly), and any free trial follow what is shown on the purchase screen and in the App Store.",
          "The subscription renews automatically. Unless cancelled at least 24 hours before the end of the current period, it renews under the same terms and you will be charged.",
          "You can manage or cancel the subscription directly in your device's App Store account settings. Billing and refunds follow Apple App Store policies.",
          "If a free trial is offered, it converts to a paid subscription unless cancelled before the trial ends.",
        ],
      },
      {
        title: "3. Data and User Responsibility",
        list: [
          "Task data is stored locally and in your own iCloud; Daydream Factory does not access its contents.",
          "Data may be lost when changing devices, deleting the app, or changing iCloud settings. You are responsible for managing and backing up important content.",
          "Using the in-app 'Reset everything' feature deletes the data on your device.",
        ],
      },
      {
        title: "4. Disclaimer",
        list: [
          "AddUp is provided 'as is'. Notification timing and syncing may be delayed or limited depending on the operating system, network, and external services.",
          "To the extent permitted by applicable law, liability for damages arising from use of the service or data loss may be limited.",
        ],
      },
      {
        title: "5. Intellectual Property",
        body:
          "Rights to the AddUp app, design, logo, and content belong to Daydream Factory. Unauthorized copying, distribution, or reverse engineering is prohibited.",
      },
      {
        title: "6. Changes to Terms",
        body:
          "These terms may be updated to improve the service or to comply with law. Significant changes will be announced via the app or this page.",
      },
      {
        title: "7. Contact",
        body: "Questions about these terms: support@daydreamfactory.net",
      },
    ],
  },
  ja: {
    crumb: "AddUp Terms",
    title: "AddUp,",
    accentTitle: "利用規約",
    subtitle:
      "最終更新日: 2026年6月14日。本サービスの運営主体は Daydream Factory です。AddUp アプリおよび関連ウェブページを利用する場合、以下の規約が適用されます。",
    sections: [
      {
        title: "1. サービスについて",
        body:
          "AddUp は会員登録なしで使える To Do・スケジュール管理アプリです。作成した To Do は端末内に保存され、ユーザー本人の iCloud アカウントを通じて端末間で同期されます。",
      },
      {
        title: "2. サブスクリプション (AddUp Pro)",
        list: [
          "AddUp は無料で利用でき、繰り返し予定・リマインダー・詳細統計などの一部機能は自動更新サブスクリプション (AddUp Pro) で提供されます。",
          "価格・期間（月額/年額）・無料トライアルは、購入画面および App Store に表示される内容に従います。",
          "サブスクリプションは自動更新され、現在の期間終了の24時間以上前に解約しない限り、同条件で更新され料金が請求されます。",
          "サブスクの管理・解約は端末の App Store アカウント設定から行えます。決済・返金は Apple App Store のポリシーに従います。",
          "無料トライアルが提供される場合、トライアル終了前に解約しないと有料サブスクへ移行します。",
        ],
      },
      {
        title: "3. データとユーザーの責任",
        list: [
          "To Do データは端末内およびユーザー本人の iCloud に保存され、Daydream Factory はその内容にアクセスしません。",
          "端末の変更・アプリの削除・iCloud 設定の変更でデータが失われる場合があるため、重要な内容はユーザーが管理・バックアップする責任があります。",
          "アプリ内の「すべて初期化」を使用すると端末内のデータが削除されます。",
        ],
      },
      {
        title: "4. 免責",
        list: [
          "AddUp は「現状のまま」提供され、通知の送信タイミングや同期は OS・通信環境・外部サービスの状態により遅延・制限される場合があります。",
          "本サービスの利用またはデータ消失により生じた損害については、適用法令が認める範囲で責任が制限される場合があります。",
        ],
      },
      {
        title: "5. 知的財産権",
        body:
          "AddUp アプリ、デザイン、ロゴ、コンテンツに関する権利は Daydream Factory に帰属し、無断複製・配布・リバースエンジニアリングを禁止します。",
      },
      {
        title: "6. 規約の変更",
        body:
          "本規約はサービス改善または法令の変更に応じて改定される場合があり、重要な変更はアプリまたは本ページでお知らせします。",
      },
      {
        title: "7. お問い合わせ",
        body: "本規約に関するお問い合わせ: support@daydreamfactory.net",
      },
    ],
  },
};

const AddUpTerms: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  React.useEffect(() => {
    setLocale(detectPageLocale(LOCALE_STORAGE_KEY, locales, "en"));
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      document.documentElement.lang = locale;
    }
  }, [locale]);

  const copy = content[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <div style={styles.topRow}>
          <Link to="/" style={styles.homeLink}>
            ← Daydream Factory
          </Link>
          <div style={styles.topRowMeta}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {locales.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setLocale(item)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === item ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[item]}
                </button>
              ))}
            </div>
            <span style={styles.crumb}>{copy.crumb}</span>
          </div>
        </div>

        <section style={styles.hero}>
          <h1 style={styles.title}>
            {copy.title}
            <br />
            <span style={styles.accentTitle}>{copy.accentTitle}</span>
          </h1>
          <p style={styles.subtitle}>{copy.subtitle}</p>
        </section>

        {copy.sections.map((section) => (
          <section key={section.title} style={styles.section}>
            <h2 style={styles.h2}>{section.title}</h2>
            <div style={styles.sectionCard}>
              {section.body ? (
                <p style={styles.body}>
                  {section.body.includes("support@daydreamfactory.net") ? (
                    <>
                      {section.body.replace("support@daydreamfactory.net", "")}
                      <a href="mailto:support@daydreamfactory.net" style={styles.link}>
                        support@daydreamfactory.net
                      </a>
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              ) : null}
              {section.list ? (
                <ul style={styles.list}>
                  {section.list.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        ))}
      </div>
    </main>
  );
};

export default AddUpTerms;
